// src/components/ViewContractDetails.tsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';

interface ContractSection {
    label: string;
    items: ContractItem[];
}

interface ContractItem {
    type: 'text' | 'field';
    label?: string;
    content?: string;
    value?: string;
}

interface ContractDetails {
    id: number;
    name: string;
    jsonData: string;
    status: string;
    lastUpdated: string;
    notes: string;
    dueDate?: string;
    signature?: string;
    clientName?: string;
}

const ViewContractDetails: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [contract, setContract] = useState<ContractDetails | null>(null);
    const [sections, setSections] = useState<ContractSection[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        fetchContract();
    }, [id]);

    const fetchContract = async () => {
        setIsLoading(true);
        setError('');
        try {
            const token = localStorage.getItem('token');
            if (!token) throw new Error('No authentication token found.');
            const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/Contracts/${id}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setContract(response.data);
            const parsedData = JSON.parse(response.data.jsonData || '{"sections":[]}');
            setSections(parsedData.sections || []);
        } catch (error: any) {
            if (error.response?.status === 401) {
                localStorage.removeItem('token');
                window.dispatchEvent(new Event('authChange'));
                navigate('/');
            } else {
                setError(error.response?.data || 'Failed to load contract details.');
            }
        } finally {
            setIsLoading(false);
        }
    };

    if (isLoading) return <div className="container mt-5"><div className="alert alert-info">Loading...</div></div>;
    if (error) return <div className="container mt-5"><div className="alert alert-danger">{error}</div></div>;
    if (!contract) return <div className="container mt-5"><div className="alert alert-warning">Contract not found.</div></div>;

    return (
        <div className="container mt-5">
            <h2 className="mb-4 text-center">Contract Details: {contract.name || `Contract ${id}`}</h2>
            <div className="card shadow-sm mb-4">
                <div className="card-header bg-primary text-white">
                    <h4 className="mb-0">Contract Information</h4>
                </div>
                <div className="card-body">
                    <p><strong>Status:</strong> {contract.status}</p>
                    <p><strong>Last Updated:</strong> {new Date(contract.lastUpdated).toLocaleString()}</p>
                    {contract.dueDate && <p><strong>Due Date:</strong> {new Date(contract.dueDate).toLocaleDateString()}</p>}
                    {contract.clientName && <p><strong>Client:</strong> {contract.clientName}</p>}
                    <p><strong>Notes:</strong> {contract.notes || 'None'}</p>
                    <p><strong>Signed:</strong> {contract.signature ? 'Yes' : 'No'}</p>
                </div>
            </div>

            <div className="card shadow-sm mb-4">
                <div className="card-header bg-primary text-white">
                    <h4 className="mb-0">Contract Content</h4>
                </div>
                <div className="card-body">
                    {sections.length === 0 ? (
                        <p className="text-muted">No sections defined in this contract.</p>
                    ) : (
                        sections.map((section, index) => (
                            <div key={index} className="mb-4">
                                <h5 className="text-primary">{section.label}</h5>
                                <div className="border rounded p-3 bg-light">
                                    {section.items.map((item, i) => (
                                        <div key={i} className="mb-2">
                                            {item.type === 'text' && <p className="mb-0">{item.content}</p>}
                                            {item.type === 'field' && (
                                                <p className="mb-0">
                                                    <strong>{item.label}:</strong>{' '}
                                                    {item.value || <span className="text-muted">Not specified</span>}
                                                </p>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </div>

            {contract.signature && (
                <div className="card shadow-sm mb-4">
                    <div className="card-header bg-primary text-white">
                        <h4 className="mb-0">Client Signature</h4>
                    </div>
                    <div className="card-body">
                        <img
                            src={contract.signature}
                            alt="Client Signature"
                            className="border rounded"
                            style={{ maxWidth: '500px' }}
                        />
                    </div>
                </div>
            )}
        </div>
    );
};

export default ViewContractDetails;